import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, HostBinding, Input, ChangeDetectionStrategy, Inject, NgModule } from '@angular/core';
import { BaseDirective } from '@eui/components/shared/base';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { EUI_CONFIG_TOKEN } from '@eui/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class EuiIconComponent extends BaseDirective {
    constructor() {
        super();
        this.role = 'img';
        this._isLoading = false;
        this._ariaLabel = 'eUI Icon';
    }
    get cssClasses() {
        return this.getCssClasses();
    }
    get ariaLabel() {
        return this._ariaLabel;
    }
    set ariaLabel(value) {
        this._ariaLabel = value;
    }
    get iconClass() { return this._iconClass; }
    set iconClass(value) { this._iconClass = value; }
    get isLoading() { return this._isLoading; }
    set isLoading(value) { this._isLoading = coerceBooleanProperty(value); }
    getCssClasses() {
        return [
            super.getCssClasses('eui-icon'),
            this.iconClass && !this.isLoading ? this.iconClass : ``,
            this.isLoading ? 'eui-icon--loading' : '',
        ].join(' ').trim();
    }
}
/** @nocollapse */ EuiIconComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiIconComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiIconComponent, selector: "div[euiIcon], span[euiIcon], i[euiIcon], eui-icon", inputs: { ariaLabel: ["aria-label", "ariaLabel"], iconClass: "iconClass", isLoading: "isLoading" }, host: { properties: { "class": "this.cssClasses", "attr.role": "this.role", "attr.aria-label": "this.ariaLabel" } }, usesInheritance: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [":host{display:inline-flex}:host .eui-icon-svg{display:inline-flex}.eui-icon--loading,.eui-icon-svg--loading{align-items:center;display:inline-flex}.eui-icon--loading:after,.eui-icon--loading:before,.eui-icon-svg--loading:after,.eui-icon-svg--loading:before{content:\"\";position:absolute;height:var(--eui-base-spacing-l);width:var(--eui-base-spacing-l)}.eui-icon--loading:before,.eui-icon-svg--loading:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.2);border-radius:100%}.eui-icon--loading:after,.eui-icon-svg--loading:after{animation:eui-icon-loading-kf .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-icon--loading--sm:before,.eui-icon--loading--sm:after,.eui-icon-svg--loading--sm:before,.eui-icon-svg--loading--sm:after{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-icon-loading-kf{to{transform:rotate(360deg)}}\n"], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconComponent, decorators: [{
            type: Component,
            args: [{ selector: 'div[euiIcon], span[euiIcon], i[euiIcon], eui-icon', template: '<ng-content></ng-content>', encapsulation: ViewEncapsulation.None, styles: [":host{display:inline-flex}:host .eui-icon-svg{display:inline-flex}.eui-icon--loading,.eui-icon-svg--loading{align-items:center;display:inline-flex}.eui-icon--loading:after,.eui-icon--loading:before,.eui-icon-svg--loading:after,.eui-icon-svg--loading:before{content:\"\";position:absolute;height:var(--eui-base-spacing-l);width:var(--eui-base-spacing-l)}.eui-icon--loading:before,.eui-icon-svg--loading:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.2);border-radius:100%}.eui-icon--loading:after,.eui-icon-svg--loading:after{animation:eui-icon-loading-kf .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-icon--loading--sm:before,.eui-icon--loading--sm:after,.eui-icon-svg--loading--sm:before,.eui-icon-svg--loading--sm:after{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-icon-loading-kf{to{transform:rotate(360deg)}}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { cssClasses: [{
                type: HostBinding,
                args: ['class']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }, {
                type: Input,
                args: ['aria-label']
            }], iconClass: [{
                type: Input
            }], isLoading: [{
                type: Input
            }] } });

class EuiIconSvgComponent extends BaseDirective {
    constructor(config) {
        super();
        this.config = config;
        this.size = 'm';
        this.fillColor = 'grey-100';
        this.set = 'eui';
        this.style = '';
        this._ariaLabel = 'eUI Icon';
        this.assetsBaseUrl = 'assets';
        this._ariaHidden = true;
        this._focusable = false;
        this._isLoading = false;
    }
    get cssClasses() {
        return 'eui-icon-svg';
    }
    get ariaLabel() {
        return this._ariaLabel;
    }
    set ariaLabel(value) {
        this._ariaLabel = value;
    }
    get ariaHidden() { return this._ariaHidden; }
    set ariaHidden(value) { this._ariaHidden = coerceBooleanProperty(value); }
    get focusable() { return this._focusable; }
    set focusable(value) { this._focusable = coerceBooleanProperty(value); }
    get isLoading() { return this._isLoading; }
    set isLoading(value) { this._isLoading = coerceBooleanProperty(value); }
    ngOnInit() {
        const assetsBaseUrl = this.config?.appConfig?.global?.eui?.assetsBaseUrl;
        if (assetsBaseUrl) {
            this.assetsBaseUrl = this.config.appConfig.global.eui.assetsBaseUrl;
        }
    }
    ngOnChanges(c) {
        if (c && c.set) {
            if (c.set.currentValue !== c.set.previousValue) {
                this._setIconAndSet();
            }
        }
        if (c && c.icon) {
            if (c.icon.currentValue !== c.icon.previousValue) {
                this._setIconAndSet();
            }
        }
    }
    ngAfterContentInit() {
        this._setIconAndSet();
    }
    getCssClasses() {
        return [
            super.getCssClasses('eui-icon-svg'),
            this.isLoading ? 'eui--loading' : '',
            `eui-icon-svg--color-${this.fillColor}`,
            `eui-icon-svg--size-${this.size}`,
        ].join(' ').trim();
    }
    _setIconAndSet() {
        if (this.icon && this.icon.indexOf(':') > -1) {
            const iconSet = this.icon.split(':');
            this.icon = iconSet[0];
            this.set = iconSet[1];
        }
        if (!this.set || this.set === '') {
            this.set = 'eui';
        }
        else {
            if (this.set === 'sharp' || this.set === 'outline') {
                this.iconName = `${this.icon}-${this.set}`;
            }
            else {
                this.iconName = this.icon;
            }
        }
    }
}
/** @nocollapse */ EuiIconSvgComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconSvgComponent, deps: [{ token: EUI_CONFIG_TOKEN }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiIconSvgComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiIconSvgComponent, selector: "eui-icon-svg, span[euiIconSvg], i[euiIconSvg]", inputs: { icon: "icon", size: "size", fillColor: "fillColor", set: "set", ariaLabelledby: "ariaLabelledby", role: "role", style: "style", iconUrl: "iconUrl", ariaLabel: ["aria-label", "ariaLabel"], ariaHidden: "ariaHidden", focusable: "focusable", isLoading: "isLoading" }, host: { properties: { "class": "this.cssClasses", "attr.aria-label": "this.ariaLabel" } }, usesInheritance: true, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"icon && !iconUrl\">\n    <ng-container *ngTemplateOutlet=\"default\"></ng-container>\n</ng-container>\n<ng-container *ngIf=\"iconUrl\">\n    <ng-container *ngTemplateOutlet=\"custom\"></ng-container>\n</ng-container>\n\n<ng-template #default>\n    <svg [ngClass]=\"getCssClasses()\"\n        [attr.aria-hidden]=\"ariaHidden\"\n        [attr.aria-labelledby]=\"ariaLabelledby || null\"\n        [attr.role]=\"role || null\"\n        [attr.focusable]=\"focusable\">\n        <use [attr.href]=\"assetsBaseUrl + '/icons/sprites/'+ set + '.svg#' + iconName\"></use>\n    </svg>\n    <ng-content></ng-content>\n</ng-template>\n\n<ng-template #custom>\n    <svg [class]=\"'eui-icon-svg eui-icon-svg--size-' + size\" [attr.style]=\"style ? style : null\"\n        [attr.aria-hidden]=\"ariaHidden\"\n        [attr.aria-labelledby]=\"ariaLabelledby || null\"\n        [attr.role]=\"role || null\"\n        [attr.focusable]=\"focusable\">\n        <image [attr.href]=\"iconUrl\" [class]=\"'eui-icon-svg eui-icon-svg--size-' + size\" [attr.style]=\"style ? style : null\"></image>\n    </svg>\n</ng-template>\n", styles: [":host{display:inline-flex}:host .eui-icon-svg{display:inline-flex}.eui-icon--loading,.eui-icon-svg--loading{align-items:center;display:inline-flex}.eui-icon--loading:after,.eui-icon--loading:before,.eui-icon-svg--loading:after,.eui-icon-svg--loading:before{content:\"\";position:absolute;height:var(--eui-base-spacing-l);width:var(--eui-base-spacing-l)}.eui-icon--loading:before,.eui-icon-svg--loading:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.2);border-radius:100%}.eui-icon--loading:after,.eui-icon-svg--loading:after{animation:eui-icon-loading-kf .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-icon--loading--sm:before,.eui-icon--loading--sm:after,.eui-icon-svg--loading--sm:before,.eui-icon-svg--loading--sm:after{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-icon-loading-kf{to{transform:rotate(360deg)}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconSvgComponent, decorators: [{
            type: Component,
            args: [{ selector: 'eui-icon-svg, span[euiIconSvg], i[euiIconSvg]', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, template: "<ng-container *ngIf=\"icon && !iconUrl\">\n    <ng-container *ngTemplateOutlet=\"default\"></ng-container>\n</ng-container>\n<ng-container *ngIf=\"iconUrl\">\n    <ng-container *ngTemplateOutlet=\"custom\"></ng-container>\n</ng-container>\n\n<ng-template #default>\n    <svg [ngClass]=\"getCssClasses()\"\n        [attr.aria-hidden]=\"ariaHidden\"\n        [attr.aria-labelledby]=\"ariaLabelledby || null\"\n        [attr.role]=\"role || null\"\n        [attr.focusable]=\"focusable\">\n        <use [attr.href]=\"assetsBaseUrl + '/icons/sprites/'+ set + '.svg#' + iconName\"></use>\n    </svg>\n    <ng-content></ng-content>\n</ng-template>\n\n<ng-template #custom>\n    <svg [class]=\"'eui-icon-svg eui-icon-svg--size-' + size\" [attr.style]=\"style ? style : null\"\n        [attr.aria-hidden]=\"ariaHidden\"\n        [attr.aria-labelledby]=\"ariaLabelledby || null\"\n        [attr.role]=\"role || null\"\n        [attr.focusable]=\"focusable\">\n        <image [attr.href]=\"iconUrl\" [class]=\"'eui-icon-svg eui-icon-svg--size-' + size\" [attr.style]=\"style ? style : null\"></image>\n    </svg>\n</ng-template>\n", styles: [":host{display:inline-flex}:host .eui-icon-svg{display:inline-flex}.eui-icon--loading,.eui-icon-svg--loading{align-items:center;display:inline-flex}.eui-icon--loading:after,.eui-icon--loading:before,.eui-icon-svg--loading:after,.eui-icon-svg--loading:before{content:\"\";position:absolute;height:var(--eui-base-spacing-l);width:var(--eui-base-spacing-l)}.eui-icon--loading:before,.eui-icon-svg--loading:before{border:var(--eui-base-spacing-2xs) solid rgba(0,0,0,.2);border-radius:100%}.eui-icon--loading:after,.eui-icon-svg--loading:after{animation:eui-icon-loading-kf .6s linear;animation-iteration-count:infinite;border-color:var(--eui-base-color-white) transparent transparent;border-radius:100%;border-style:solid;border-width:var(--eui-base-spacing-2xs);box-shadow:0 0 0 1px transparent}.eui-icon--loading--sm:before,.eui-icon--loading--sm:after,.eui-icon-svg--loading--sm:before,.eui-icon-svg--loading--sm:after{height:var(--eui-base-spacing-m);width:var(--eui-base-spacing-m)}@keyframes eui-icon-loading-kf{to{transform:rotate(360deg)}}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EUI_CONFIG_TOKEN]
                }] }]; }, propDecorators: { cssClasses: [{
                type: HostBinding,
                args: ['class']
            }], icon: [{
                type: Input
            }], size: [{
                type: Input
            }], fillColor: [{
                type: Input
            }], set: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], role: [{
                type: Input
            }], style: [{
                type: Input
            }], iconUrl: [{
                type: Input
            }], ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }, {
                type: Input,
                args: ['aria-label']
            }], ariaHidden: [{
                type: Input
            }], focusable: [{
                type: Input
            }], isLoading: [{
                type: Input
            }] } });

class EuiIconColorComponent {
    constructor() {
        this.paths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
    }
    get cssClasses() {
        return this.getCssClasses();
    }
    getCssClasses() {
        return [
            'eui-icon eui-icon-color',
            this.iconClass ? this.iconClass : ``,
        ].join(' ').trim();
    }
}
/** @nocollapse */ EuiIconColorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconColorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ EuiIconColorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: EuiIconColorComponent, selector: "eui-icon-colored, span[euiIconColored], [euiIconColored]", inputs: { iconClass: "iconClass" }, host: { properties: { "class": "this.cssClasses" } }, ngImport: i0, template: `
    <span [class]="iconClass">
        <span *ngFor="let pathNumber of paths" class="path{{pathNumber}}"></span>
    </span>
    `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconColorComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'eui-icon-colored, span[euiIconColored], [euiIconColored]',
                    template: `
    <span [class]="iconClass">
        <span *ngFor="let pathNumber of paths" class="path{{pathNumber}}"></span>
    </span>
    `,
                }]
        }], propDecorators: { cssClasses: [{
                type: HostBinding,
                args: ['class']
            }], iconClass: [{
                type: Input
            }] } });

class EuiIconModule {
}
/** @nocollapse */ EuiIconModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ EuiIconModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: EuiIconModule, declarations: [EuiIconComponent, EuiIconSvgComponent, EuiIconColorComponent], imports: [CommonModule], exports: [EuiIconComponent, EuiIconSvgComponent, EuiIconColorComponent] });
/** @nocollapse */ EuiIconModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: EuiIconModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [EuiIconComponent, EuiIconSvgComponent, EuiIconColorComponent],
                    declarations: [EuiIconComponent, EuiIconSvgComponent, EuiIconColorComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EuiIconColorComponent, EuiIconComponent, EuiIconModule, EuiIconSvgComponent };
